import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../../context/AuthContext";
import { toast } from "sonner";
import { Brain, CheckCircle2, Timer, Swords } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ShareButton from "../share/ShareButton";

export default function QuizPlayer({ quizzes, stages = [], done, requireLogin, onDone, shareCtx }) {
  const [active, setActive] = useState(null);
  if (active) return <Runner quiz={active} onExit={() => { setActive(null); onDone(); }} shareCtx={shareCtx} />;
  const sections = [
    ...stages.map(s => ({ key: s.id, title: s.name, sub: s.km ? `${s.km} km` : "", items: quizzes.filter(q => q.stage_id === s.id) })).filter(s => s.items.length),
    { key: "general", title: "Quiz generali", sub: "", items: quizzes.filter(q => !q.stage_id || !stages.some(s => s.id === q.stage_id)) },
  ].filter(s => s.items.length);
  const completedCount = quizzes.filter(q => done.includes(`QUIZ:${q.id}`)).length;
  return (
    <div>
      <div className="rl-banner rl-watermark p-6 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="quiz-header">
        <div className="relative">
          <div className="pill-badge pill-amber mb-2"><Brain className="w-3 h-3" /> QUIZ A TEMPO</div>
          <div className="font-display font-black text-2xl">Una sezione per ogni prova speciale.</div>
          <div className="text-sm text-slate-400">15 secondi a domanda, bonus velocità. Completa i quiz di tutte le PS per il massimo punteggio.</div>
        </div>
        <div className="relative font-mono-rl text-right"><div className="text-xs text-slate-400 tracking-widest">COMPLETATI</div><div className="font-display font-black text-3xl" data-testid="quiz-completed">{completedCount}/{quizzes.length}</div></div>
      </div>
      {quizzes.length === 0 && <div className="rl-card p-8 text-slate-500 text-center">Nessun quiz disponibile.</div>}
      {sections.map(sec => (
        <div key={sec.key} className="mb-8" data-testid={`quiz-section-${sec.key}`}>
          <div className="flex items-center gap-3 mb-3"><div className="font-display font-black text-xl">{sec.title}</div>{sec.sub && <span className="font-mono-rl text-xs text-slate-500">{sec.sub}</span>}<div className="flex-1 h-px bg-white/5" /></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{sec.items.map(q => <QuizCard key={q.id} q={q} done={done} requireLogin={requireLogin} setActive={setActive} shareCtx={shareCtx} />)}</div>
        </div>
      ))}
    </div>
  );
}

function QuizCard({ q, done, requireLogin, setActive, shareCtx }) {
  const completed = done.includes(`QUIZ:${q.id}`);
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const create = async (confirm) => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/battles`, { quiz_id: q.id, hours: 48, confirm }); toast.success(data.charge?.charged ? `Usati ${data.charge.charged} RC` : "Sfida creata: gioca il tuo turno, poi condividi il link"); nav(`/challenge/${data.code}`); }
    catch (e) { const s = e.response?.status; if (s === 402) { toast.error("Hai utilizzato le tue partite gratuite di oggi.", { action: { label: "ACQUISTA CREDITI", onClick: () => nav("/store") } }); } else toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); setQuote(null); }
  };
  const battle = async () => {
    if (!requireLogin()) return;
    try { const { data } = await axios.post(`${API}/credits/quote`, { rule: "QUIZ_BATTLE" }); if (data.free_left > 0 || !data.needs_confirm) return create(true); setQuote(data); }
    catch { create(false); }
  };
  return (
    <div className="rl-card p-6 flex flex-col" data-testid={`quiz-card-${q.id}`}>
      {q.sponsor && <div className="pill-badge pill-amber self-start mb-3">POWERED BY {q.sponsor.toUpperCase()}</div>}
      <div className="font-display font-black text-2xl mb-1">{q.title}</div>
      <div className="text-xs font-mono-rl text-slate-400 mb-5">{q.questions.length} DOMANDE · {q.seconds_per_question}s · {q.points_per_correct} PTS/RISPOSTA</div>
      {completed ? (
        <div className="mt-auto flex items-center gap-2 text-[#30D158] text-sm font-mono-rl"><CheckCircle2 className="w-4 h-4" /> COMPLETATO</div>
      ) : (
        <button className="btn-crimson mt-auto w-full text-sm" data-testid={`start-quiz-${q.id}`}
          onClick={() => requireLogin() && setActive(q)}><Brain className="w-4 h-4 inline mr-2" />GIOCA</button>
      )}
      <button className="btn-outline-rl w-full text-xs mt-2 min-h-[44px]" disabled={busy} onClick={battle} data-testid={`battle-quiz-${q.id}`}><Swords className="w-4 h-4 inline mr-1" /> SFIDA UN AMICO (QUIZ BATTLE)</button>
      {quote && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-5" onClick={() => setQuote(null)} data-testid="spend-confirm">
          <div className="rl-card p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="pill-badge pill-amber mb-2">RALLY CREDITS</div>
            <h3 className="font-display font-black text-xl mb-2">Vuoi utilizzare {quote.cost} RC per questa Quiz Battle?</h3>
            <p className="text-sm text-slate-400 mb-1">Hai utilizzato le tue partite gratuite di oggi. Saldo dopo l'operazione: <b className="text-white">{quote.balance_after} RC</b></p>
            <p className="text-[10px] text-slate-500 mb-4">Le sfide extra non assegnano punti al Campionato Fan: giochi di più, non vinci di più.</p>
            <div className="flex flex-wrap gap-2">
              {quote.balance >= quote.cost ? <button className="btn-crimson !py-2 !px-4 text-xs min-h-[44px]" onClick={() => create(true)} data-testid="spend-confirm-yes">USA {quote.cost} RC</button>
                : <button className="btn-crimson !py-2 !px-4 text-xs min-h-[44px]" onClick={() => nav("/store")} data-testid="spend-buy">ACQUISTA CREDITI</button>}
              <button className="btn-outline-rl !py-2 !px-4 text-xs min-h-[44px]" onClick={() => setQuote(null)} data-testid="spend-confirm-cancel">ANNULLA · TORNA DOMANI</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function Runner({ quiz, onExit, shareCtx }) {
  const limit = quiz.seconds_per_question * 1000;
  const [idx, setIdx] = useState(0);
  const [left, setLeft] = useState(limit);
  const [answers, setAnswers] = useState([]);
  const [times, setTimes] = useState([]);
  const [result, setResult] = useState(null);
  const start = useRef(Date.now());

  useEffect(() => {
    if (result) return;
    const iv = setInterval(() => {
      const el = Date.now() - start.current;
      setLeft(Math.max(0, limit - el));
      if (el >= limit) answer(-1);
    }, 100);
    return () => clearInterval(iv);
  }, [idx, result]);

  const answer = (i) => {
    const t = Date.now() - start.current;
    const na = [...answers, i], nt = [...times, t];
    setAnswers(na); setTimes(nt);
    if (idx + 1 < quiz.questions.length) { setIdx(idx + 1); start.current = Date.now(); setLeft(limit); }
    else submit(na, nt);
  };

  const submit = async (na, nt) => {
    try {
      const { data } = await axios.post(`${API}/paddock/quizzes/${quiz.id}/submit`, { answers: na, times_ms: nt });
      setResult(data);
      toast.success(`+${data.points} punti!`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); onExit(); }
  };

  if (result) return (
    <div className="rl-card p-10 max-w-xl mx-auto text-center" data-testid="quiz-result">
      <div className="pill-badge pill-amber mb-4">QUIZ COMPLETATO</div>
      <div className="font-display font-black text-6xl text-[#FF9F0A] mb-2" data-testid="quiz-result-points">+{result.points}</div>
      <div className="text-slate-300 mb-6">{result.correct}/{result.total} risposte corrette</div>
      <div className="flex flex-wrap gap-2 justify-center">
        {shareCtx && <ShareButton kind="quiz" variant="solid" className="!py-2 !px-4 text-xs" label="CONDIVIDI IL RISULTATO" testid="share-quiz-btn"
          card={{ kicker: `RALLY QUIZ · ${shareCtx.rally.name}`, lines: [`${result.correct}/${result.total} risposte esatte`, `+${result.points} punti FantaRally`, quiz.title], cta: "PARTECIPA AL QUIZ", nickname: shareCtx.nick, rallyLogo: shareCtx.rally.logo_url }}
          share={{ target: `/paddock/${shareCtx.rallyId}?tab=QUIZ`, title: `${shareCtx.nick}: ${result.correct}/${result.total} al Rally Quiz · ${shareCtx.rally.name}`, description: `+${result.points} punti FantaRally. Riesci a fare meglio? Gioca il quiz nel Paddock.`, rally_id: shareCtx.rallyId }}
          caption={`${result.correct}/${result.total} al Rally Quiz "${quiz.title}" del ${shareCtx.rally.name}, +${result.points} punti! Riesci a fare meglio? [link]`} />}
        <button className="btn-outline-rl !py-2 !px-4 text-xs" onClick={onExit} data-testid="quiz-back-btn">TORNA AL PADDOCK</button>
      </div>
    </div>
  );

  const q = quiz.questions[idx];
  const pct = (left / limit) * 100;
  return (
    <div className="rl-card p-8 max-w-2xl mx-auto" data-testid="quiz-runner">
      <div className="flex items-center justify-between mb-4 text-xs font-mono-rl text-slate-400 tracking-widest">
        <span>DOMANDA {idx + 1}/{quiz.questions.length}</span>
        <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> {(left / 1000).toFixed(1)}s</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full mb-6 overflow-hidden">
        <div className="h-full bg-[#FF1E27] transition-[width] duration-100" style={{ width: `${pct}%` }} />
      </div>
      <div className="font-display font-bold text-2xl mb-6" data-testid="quiz-question">{q.question}</div>
      <div className="grid gap-3">
        {q.options.map((o, i) => (
          <button key={i} onClick={() => answer(i)} data-testid={`quiz-option-${i}`}
            className="text-left border border-white/10 rounded-lg px-5 py-4 hover:border-[#FF1E27] hover:bg-[#FF1E27]/10 transition-colors">
            <span className="font-mono-rl text-[#FF9F0A] mr-3">{"ABCD"[i]}</span>{o}
          </button>
        ))}
      </div>
    </div>
  );
}
